/**
 * Key Findings Cards - Strength, Watch Item, and Flag cards.
 *
 * Displays the top 3 key findings from the executive assessment.
 */

import { KeyFinding, FINDING_COLORS } from './types';

interface KeyFindingsCardsProps {
  findings?: KeyFinding[];
  keyFacts?: string[];
  careerHighlights?: string[];
}

function FindingCard({ finding }: { finding: KeyFinding }) {
  const colors = FINDING_COLORS[finding.finding_type];
  const typeLabels = {
    strength: 'Strength',
    watch_item: 'Watch Item',
    flag: 'Flag',
  };

  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} p-4`}>
      <div className="flex items-start gap-2">
        <span className={`text-lg ${colors.text}`}>{colors.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold uppercase tracking-wide ${colors.text}`}>
              {typeLabels[finding.finding_type]}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-gray-900 mt-1">{finding.title}</h4>
          <p className="text-sm text-gray-600 mt-1">{finding.description}</p>
        </div>
      </div>
    </div>
  );
}

function DerivedFindingCard({
  type,
  title,
  description,
}: {
  type: 'strength' | 'watch_item' | 'flag';
  title: string;
  description: string;
}) {
  const colors = FINDING_COLORS[type];
  const typeLabels = {
    strength: 'Strength',
    watch_item: 'Watch Item',
    flag: 'Flag',
  };

  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} p-4`}>
      <div className="flex items-start gap-2">
        <span className={`text-lg ${colors.text}`}>{colors.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold uppercase tracking-wide ${colors.text}`}>
              {typeLabels[type]}
            </span>
            <span className="text-xs text-gray-400">(derived from bio)</span>
          </div>
          <h4 className="text-sm font-semibold text-gray-900 mt-1">{title}</h4>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
      </div>
    </div>
  );
}

export function KeyFindingsCards({ findings = [], keyFacts = [], careerHighlights = [] }: KeyFindingsCardsProps) {
  // If we have actual findings from assessment, use those
  if (findings.length > 0) {
    // Get one of each type if available
    const strength = findings.find(f => f.finding_type === 'strength');
    const watchItem = findings.find(f => f.finding_type === 'watch_item');
    const flag = findings.find(f => f.finding_type === 'flag');
    const displayFindings = [strength, watchItem, flag].filter(Boolean) as KeyFinding[];

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Key Findings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {displayFindings.map((finding, idx) => (
            <FindingCard key={idx} finding={finding} />
          ))}
        </div>
      </div>
    );
  }

  // Fall back to deriving findings from bio data
  const derivedFindings: { type: 'strength' | 'watch_item' | 'flag'; title: string; description: string }[] = [];

  // Derive a strength from career highlights
  const firstHighlight = careerHighlights[0];
  const firstFact = keyFacts[0];

  if (firstHighlight) {
    derivedFindings.push({
      type: 'strength',
      title: 'Career Highlights',
      description: firstHighlight,
    });
  } else if (firstFact) {
    derivedFindings.push({
      type: 'strength',
      title: 'Key Qualification',
      description: firstFact,
    });
  }

  // Add a watch item placeholder
  derivedFindings.push({
    type: 'watch_item',
    title: 'Assessment Pending',
    description: 'Full executive assessment will identify specific watch items to monitor.',
  });

  // Add a flag placeholder
  derivedFindings.push({
    type: 'flag',
    title: 'No Flags Identified',
    description: 'Legal and regulatory screening will flag any concerns when completed.',
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Key Findings</h3>
        <span className="text-xs text-gray-500">Preliminary - Full assessment pending</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {derivedFindings.map((finding, idx) => (
          <DerivedFindingCard
            key={idx}
            type={finding.type}
            title={finding.title}
            description={finding.description}
          />
        ))}
      </div>
    </div>
  );
}

export default KeyFindingsCards;
